import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, TextField, TextArea } from '@heroui/react';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { uploadFile } from '@/lib/uploader';
import { LIMITS } from '@bunker/shared';

/**
 * "Beta" feedback — a user reports a bug or an inaccuracy while Bunker is in
 * early access. Description + an optional screenshot; lands in the platform
 * moderation panel (api.createBugReport). Controlled by the caller (the Beta
 * button in HeroUserWidget owns the open state).
 */
export function BugReportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [shot, setShot] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const canSend = description.trim().length >= LIMITS.bugReport.descriptionMin && !sending;

  function reset() {
    setDescription('');
    clearShot();
  }

  function clearShot() {
    setShot(null);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInput.current) fileInput.current.value = '';
  }

  function close() {
    if (sending) return;
    reset();
    onClose();
  }

  function pickScreenshot(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('beta.notImage'));
      return;
    }
    if (file.size > LIMITS.attachment.sizeBytes) {
      toast.error(t('beta.tooLarge'));
      return;
    }
    clearShot();
    setShot(file);
    setPreview(URL.createObjectURL(file));
  }

  async function submit() {
    if (!canSend) return;
    setSending(true);
    try {
      let screenshotUploadId: string | undefined;
      if (shot) {
        const uploaded = await uploadFile(shot, { fileId: crypto.randomUUID() });
        screenshotUploadId = uploaded.id;
      }
      await api.createBugReport({
        description: description.trim(),
        ...(screenshotUploadId ? { screenshotUploadId } : {}),
        path: `${window.location.pathname}${window.location.search}`.slice(0, 512),
      });
      toast.success(t('beta.sent'));
      reset();
      onClose();
    } catch (err) {
      toast.error(...errToToast(err, t('beta.error')));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={(o) => !o && close()}>
      <Modal.Container scroll="inside" size="sm">
        <Modal.Dialog aria-label={t('beta.title')}>
          <Modal.Header>
            <div className="flex min-w-0 items-center gap-2">
              <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-accent">
                Beta
              </span>
              <span>{t('beta.title')}</span>
            </div>
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-4">
            <TextField fullWidth aria-label={t('beta.descriptionLabel')}>
              <TextArea
                variant="secondary"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={LIMITS.bugReport.descriptionMax}
                placeholder={t('beta.descriptionPlaceholder')}
                autoFocus
              />
            </TextField>

            {/* Optional screenshot */}
            <div className="flex flex-col gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => pickScreenshot(e.target.files?.[0] ?? undefined)}
              />
              {preview ? (
                <div className="relative w-fit">
                  <img
                    src={preview}
                    alt=""
                    className="max-h-40 rounded-xl border border-separator object-contain"
                  />
                  <button
                    type="button"
                    onClick={clearShot}
                    aria-label={t('beta.removeScreenshot')}
                    className="absolute -right-2 -top-2 grid size-6 place-items-center rounded-full bg-surface text-ink shadow-overlay ring-1 ring-separator hover:bg-default/60"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  className="m-0.5 w-fit"
                  onPress={() => fileInput.current?.click()}
                >
                  {t('beta.attachScreenshot')}
                </Button>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onPress={close} isDisabled={sending}>
              {t('beta.cancel')}
            </Button>
            <Button variant="primary" onPress={() => void submit()} isDisabled={!canSend} isPending={sending}>
              {t('beta.submit')}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
